import { Loading } from "modules/shared/components/Loading"
import { useConversationSupportEntitiesQuery } from "modules/support/hooks/useConversationSupportEntitiesQuery"
import { scrollToSupportEntityMarker } from "modules/support/lib/supportEntityMarker"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { ActionRequestEntityRow } from "./ActionRequestEntityRow"
import { IssueUpdateRow } from "./IssueUpdateRow"
import { QuestionEntityRow } from "./QuestionEntityRow"

type Props = {
  conversationId: string
  createdAtFrom?: string
}

export function ConversationSupportTimeline({ conversationId, createdAtFrom }: Props) {
  const { t } = useTranslation()
  const { data, isPending } = useConversationSupportEntitiesQuery({
    query: { conversationId },
  })
  const supportEntities = useMemo(
    () => data?.filter((item) => !createdAtFrom || item.createdAt >= createdAtFrom) ?? [],
    [createdAtFrom, data],
  )

  let body = (
    <p className="text-sm text-muted-foreground">{t("support.entities.conversationEmpty")}</p>
  )

  if (isPending) {
    body = <Loading />
  } else if (supportEntities.length > 0) {
    body = (
      <div className="flex flex-col">
        {supportEntities.map((item, index) => {
          const isFirst = index === 0
          const isLast = index === supportEntities.length - 1

          switch (item.kind) {
            case "question":
              return (
                <QuestionEntityRow
                  key={`${item.kind}-${item.id}`}
                  item={item}
                  isFirst={isFirst}
                  isLast={isLast}
                  onClick={() => scrollToSupportEntityMarker(item.kind, item.id)}
                />
              )
            case "actionRequest":
              return (
                <ActionRequestEntityRow
                  key={`${item.kind}-${item.id}`}
                  item={item}
                  isFirst={isFirst}
                  isLast={isLast}
                  onClick={() => scrollToSupportEntityMarker(item.kind, item.id)}
                />
              )
            case "issueUpdate":
              return (
                <IssueUpdateRow
                  key={`${item.kind}-${item.id}`}
                  item={item}
                  isFirst={isFirst}
                  isLast={isLast}
                  onClick={() => scrollToSupportEntityMarker(item.kind, item.id)}
                />
              )
          }
        })}
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-4">
      <span className="text-lg">{t("support.entities.conversationTitle")}</span>
      {body}
    </div>
  )
}
